import React from "react";
import { getDocumentTitle } from "@ca-dmv-radv/data";
import {
  Appointments,
  ConfirmResidencyName,
  Dashboard,
  ErrorScreen,
  FieldOffices,
  FinalConfirmation,
  Identity,
  IdentityNameChanges,
  MDLHome,
  DynamicDashboard,
  DynamicAppsDashboard,
  DynamicFinalConfirmation,
  DynamicErrorScreen,
  MDLSearch,
  DynamicExpiredScreen,
  MDLNoVerification,
  MobileSessionDv,
  DynamicMobileSession,
  SmartEdlSession,
  PageNotFound,
  PresenceVerificationLanding,
  SaveAndExit,
  Timeout,
  UnderMaintenance,
  UnderReview,
  MDLLandingPage,
  GenericNameConfirmation,
  GenericNameChanges,
  GetStarted,
} from "@ca-dmv-radv/screens";
import NCHPaymentLanding from "@ca-dmv-radv/screens/src/nch-landing";
import RNCPaymentLanding from "@ca-dmv-radv/screens/src/rnc-landing";
import { TokenCapture } from "./TokenCapture";

const mobileSessionTypes = [
  "mdl",
  "odp",
  "vdl",
  "nch",
  "rvd",
  "rvo",
  "rpc",
  "rnc",
];

/**
 * Routes for the application.
 */
const routes = [
  {
    path: "/",
    title: getDocumentTitle("Dashboard"),
    Component: Dashboard,
    eventName: "RADVdocumentVerification",
  },
  {
    path: "/identity",
    title: getDocumentTitle("Identity Document"),
    Component: Identity,
    eventName: "RADVIdentityProofStart",
  },
  {
    path: "/identity/name-changes",
    title: getDocumentTitle("Name Change Documents"),
    Component: IdentityNameChanges,
    eventName: "RADVIdentityNameChanges",
  },
  {
    path: "/residency/doc1",
    title: getDocumentTitle("Confirm Residency Name"),
    Component: () => <ConfirmResidencyName docNumber={1} />,
    eventName: "RADVStateResidencyDoc1Start",
  },
  {
    path: "/residency/doc2",
    title: getDocumentTitle("Confirm Residency Name"),
    Component: () => <ConfirmResidencyName docNumber={2} />,
    eventName: "RADVStateResidencyDoc2Start",
  },
  {
    path: "/appointments",
    title: getDocumentTitle("Appointments"),
    Component: Appointments,
    eventName: "RADVAppointments",
  },
  {
    path: "/appointment-location",
    title: getDocumentTitle("Appointment Location"),
    Component: FieldOffices,
    eventName: "RADVAppointmentLocation",
  },
  {
    path: "/error",
    title: "Error",
    Component: ErrorScreen,
    eventName: "RADVError",
  },
  {
    path: "/under-review",
    title: "Under Review",
    Component: UnderReview,
    eventName: "RADVUnderReview",
  },
  {
    path: "/under-maintenance",
    title: "Under Maintenance",
    Component: UnderMaintenance,
    eventName: "RADVUnderMaintenance",
  },
  {
    path: "/final-confirmation",
    title: getDocumentTitle("Final Confirmation"),
    Component: FinalConfirmation,
    eventName: "RADVFinalConfirmation",
  },
  {
    path: "/saved",
    title: getDocumentTitle("Saved"),
    Component: () => <SaveAndExit />,
    eventName: "RADVdocumentVerificationComplete",
  },
  {
    path: "/log-out",
    title: "Log Out",
    Component: () => <SaveAndExit isLogout />,
  },
  {
    path: "/auto-saved",
    title: getDocumentTitle("Saved"),
    Component: () => <UnderReview autoSubmit />,
    eventName: "RADVdocumentVerificationComplete",
  },
  {
    path: "/presence-verification",
    title: "Presence Verification Landing Page",
    Component: PresenceVerificationLanding,
    eventName: "RADVpresenceVerificationLandingPage",
  },
  {
    path: "/mdl",
    title: "mDL",
    Component: MDLHome,
  },
  {
    path: "/*",
    title: getDocumentTitle("Page Not Found"),
    Component: PageNotFound,
    eventName: "RADVPageNotFound",
  },
];

export const unauthenticatedRoutes = [
  {
    path: "/token",
    title: getDocumentTitle("Token Capture"),
    Component: TokenCapture,
  },
  {
    path: "/getStarted",
    title: getDocumentTitle("Get Started"),
    Component: GetStarted,
  },
  {
    path: "/token/:token",
    title: getDocumentTitle("Token Capture"),
    Component: TokenCapture,
  },
  {
    path: "/token/:token/:body",
    title: getDocumentTitle("Token Capture"),
    Component: TokenCapture,
  },
  {
    path: "/ms/:token",
    title: getDocumentTitle("Mobile Session"),
    Component: MobileSessionDv,
  },
  ...mobileSessionTypes.map((appType) => ({
    path: `/ms/${appType}/:token`,
    title: getDocumentTitle("Mobile Session"),
    Component: () => <DynamicMobileSession applicationType={appType} />,
  })),
  ...mobileSessionTypes.map((appType) => ({
    path: `/ms/${appType}/sedl/:token`,
    title: getDocumentTitle("Mobile Session"),
    Component: () => <SmartEdlSession applicationType={appType} />,
  })),
  {
    path: `/ms/sedl/:token`,
    title: getDocumentTitle("Mobile Session"),
    Component: () => <SmartEdlSession applicationType="dv" />,
  },
  {
    path: "/ms/start/:token",
    title: getDocumentTitle("Begin MDL Flow"),
    Component: MDLLandingPage,
    eventName: "MDLLanding",
  },
  {
    path: "/timeout",
    title: getDocumentTitle("Timeout"),
    Component: () => <Timeout applicationType="dv" />,
  },
  {
    path: "/dv/expired",
    title: getDocumentTitle("Expired"),
    Component: MobileSessionDv,
  },
  {
    path: "/mdl/error",
    title: getDocumentTitle("Error"),
    Component: DynamicErrorScreen,
  },
  {
    path: "/mdl/search",
    title: getDocumentTitle("Search"),
    Component: () => <MDLSearch applicationType="mdl" />,
    eventName: "MDLSearch",
  },
  {
    path: "/mdl/expired",
    title: getDocumentTitle("Expired"),
    Component: () => <DynamicExpiredScreen applicationType="mdl" />,
    eventName: "MDLExpired",
  },
  {
    path: "/mdl/timeout",
    title: getDocumentTitle("Timeout"),
    Component: () => <Timeout applicationType="mdl" />,
    eventName: "MDLTimeout",
  },
  {
    path: "/mdl/under-maintenance",
    title: "Under Maintenance",
    Component: UnderMaintenance,
    eventName: "MDLUnderMaintenance",
  },
  {
    path: "/odp/error",
    title: getDocumentTitle("Error"),
    Component: DynamicErrorScreen,
    eventName: "ODPError",
  },
  {
    path: "/odp/expired",
    title: getDocumentTitle("Expired"),
    Component: () => <DynamicExpiredScreen applicationType="odp" />,
    eventName: "ODPExpired",
  },
  {
    path: "/odp/timeout",
    title: getDocumentTitle("Timeout"),
    Component: () => <Timeout applicationType="odp" />,
    eventName: "ODPTimeout",
  },
  {
    path: "/odp/under-maintenance",
    title: getDocumentTitle("Under Maintenance"),
    Component: UnderMaintenance,
    eventName: "ODPUnderMaintenance",
  },
  {
    path: "/vdl/error",
    title: getDocumentTitle("Error"),
    Component: DynamicErrorScreen,
    eventName: "VDLError",
  },
  {
    path: "/vdl/expired",
    title: getDocumentTitle("Expired"),
    Component: () => <DynamicExpiredScreen applicationType="vdl" />,
    eventName: "VDLExpired",
  },
  {
    path: "/vdl/timeout",
    title: getDocumentTitle("Timeout"),
    Component: () => <Timeout applicationType="vdl" />,
    eventName: "VDLTimeout",
  },
  {
    path: "/vdl/under-maintenance",
    title: getDocumentTitle("Under Maintenance"),
    Component: UnderMaintenance,
    eventName: "VDLUnderMaintenance",
  },
  {
    path: "/vdl/search",
    title: getDocumentTitle("Search"),
    Component: () => <MDLSearch applicationType="vdl" />,
    eventName: "VDLSearch",
  },
  {
    path: "/nch/error",
    title: getDocumentTitle("Error"),
    Component: DynamicErrorScreen,
    eventName: "NCHError",
  },
  {
    path: "/nch/expired",
    title: getDocumentTitle("Expired"),
    Component: () => <DynamicExpiredScreen applicationType="nch" />,
    eventName: "NCHExpired",
  },
  {
    path: "/nch/timeout",
    title: getDocumentTitle("Timeout"),
    Component: () => <Timeout applicationType="nch" />,
    eventName: "NCHTimeout",
  },
  {
    path: "/nch/under-maintenance",
    title: getDocumentTitle("Timeout"),
    Component: UnderMaintenance,
    eventName: "NCHUnderMaintenance",
  },
  {
    path: "/rnc/error",
    title: getDocumentTitle("Error"),
    Component: DynamicErrorScreen,
    eventName: "RNCError",
  },
  {
    path: "/rnc/expired",
    title: getDocumentTitle("Expired"),
    Component: () => <DynamicExpiredScreen applicationType="rnc" />,
    eventName: "RNCExpired",
  },
  {
    path: "/rnc/timeout",
    title: getDocumentTitle("Timeout"),
    Component: () => <Timeout applicationType="rnc" />,
    eventName: "RNCTimeout",
  },
  {
    path: "/rnc/under-maintenance",
    title: getDocumentTitle("Timeout"),
    Component: UnderMaintenance,
    eventName: "RNCUnderMaintenance",
  },
  {
    path: "/rvd/error",
    title: getDocumentTitle("Error"),
    Component: DynamicErrorScreen,
    eventName: "RVDError",
  },
  {
    path: "/rvd/search",
    title: getDocumentTitle("Search"),
    Component: () => <MDLSearch applicationType="rvd" />,
    eventName: "MDLSearch",
  },
  {
    path: "/rvd/expired",
    title: getDocumentTitle("Expired"),
    Component: () => <DynamicExpiredScreen applicationType="rvd" />,
    eventName: "RVDExpired",
  },
  {
    path: "/rvd/timeout",
    title: getDocumentTitle("Timeout"),
    Component: () => <Timeout applicationType="rvd" />,
    eventName: "RVDTimeout",
  },
  {
    path: "/rvd/under-maintenance",
    title: getDocumentTitle("Under Maintenance"),
    Component: UnderMaintenance,
    eventName: "RVDUnderMaintenance",
  },
  {
    path: "/rpc/error",
    title: getDocumentTitle("Error"),
    Component: DynamicErrorScreen,
    eventName: "RPCError",
  },
  {
    path: "/rpc/expired",
    title: getDocumentTitle("Expired"),
    Component: () => <DynamicExpiredScreen applicationType="rpc" />,
    eventName: "RPCExpired",
  },
  {
    path: "/rpc/timeout",
    title: getDocumentTitle("Timeout"),
    Component: () => <Timeout applicationType="rpc" />,
    eventName: "RPCTimeout",
  },
  {
    path: "/rpc/under-maintenance",
    title: getDocumentTitle("Under Maintenance"),
    Component: UnderMaintenance,
    eventName: "RPCUnderMaintenance",
  },
  {
    path: "/rvo/error",
    title: getDocumentTitle("Error"),
    Component: DynamicErrorScreen,
    eventName: "RVOError",
  },
  {
    path: "/rvo/search",
    title: getDocumentTitle("Search"),
    Component: () => <MDLSearch applicationType="rvo" />,
    eventName: "MDLSearch",
  },
  {
    path: "/rvo/expired",
    title: getDocumentTitle("Expired"),
    Component: () => <DynamicExpiredScreen applicationType="rvo" />,
    eventName: "RVOExpired",
  },
  {
    path: "/rvo/timeout",
    title: getDocumentTitle("Timeout"),
    Component: () => <Timeout applicationType="rvo" />,
    eventName: "RVOTimeout",
  },
  {
    path: "/rvo/under-maintenance",
    title: getDocumentTitle("Under Maintenance"),
    Component: UnderMaintenance,
    eventName: "RVOUnderMaintenance",
  },
  {
    path: "/*",
    title: getDocumentTitle("Page Not Found"),
    Component: PageNotFound,
  },
];
export const mdlRoutes = [
  {
    path: "/document-upload",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicDashboard applicationType="mdl" />,
    eventName: "MobileDiversLicence",
  },
  {
    path: "/presence-verification",
    title: getDocumentTitle("Dashboard"),
    Component: () => <PresenceVerificationLanding program="mdl" />,
    eventName: "MobileDiversLicence",
  },
  {
    path: "/getStarted",
    title: getDocumentTitle("Get Started"),
    Component: GetStarted,
  },
  {
    path: "/final-confirmation",
    title: getDocumentTitle("Final Confirmation"),
    Component: () => <DynamicFinalConfirmation applicationType="mdl" />,
    eventName: "MDLFinalConfirmation",
  },
  {
    path: "/no-verification",
    title: getDocumentTitle("No Verification Needed"),
    Component: MDLNoVerification,
    eventName: "MDLNoVerification",
  },
];
export const odpRoutes = [
  {
    path: "/presence-verification",
    title: getDocumentTitle("Dashboard"),
    Component: () => <PresenceVerificationLanding program="odp" />,
    eventName: "ODPPresenceVerification",
  },
  {
    path: "/getStarted",
    title: getDocumentTitle("Get Started"),
    Component: GetStarted,
  },
  {
    path: "/final-confirmation",
    title: getDocumentTitle("Final Confirmation"),
    Component: () => <DynamicFinalConfirmation applicationType="odp" />,
    eventName: "MDLFinalConfirmation",
  },
];
export const vdlRoutes = [
  {
    path: "/presence-verification",
    title: getDocumentTitle("Presence Verification"),
    Component: () => <PresenceVerificationLanding program="vdl" />,
    eventName: "VDLPresenceVerification",
  },
  {
    path: "/getStarted",
    title: getDocumentTitle("Get Started"),
    Component: GetStarted,
  },
  {
    path: "/document-upload",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicDashboard applicationType="vdl" />,
    eventName: "VDLDashboard",
  },
  {
    path: "/final-confirmation",
    title: getDocumentTitle("Final Confirmation"),
    Component: () => <DynamicFinalConfirmation applicationType="vdl" />,
    eventName: "VDLFinalConfirmation",
  },
];
export const rvoRoutes = [
  {
    path: "/presence-verification",
    title: getDocumentTitle("Presence Verification"),
    Component: () => <PresenceVerificationLanding program="rvo" />,
    eventName: "RVOPresenceVerification",
  },
  {
    path: "/getStarted",
    title: getDocumentTitle("Get Started"),
    Component: GetStarted,
  },
  {
    path: "/document-upload",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicDashboard applicationType="rvo" />,
    eventName: "RVODashboard",
  },
  {
    path: "/final-confirmation",
    title: getDocumentTitle("Final Confirmation"),
    Component: () => <DynamicFinalConfirmation applicationType="rvo" />,
    eventName: "RVOFinalConfirmation",
  },
];
export const rvdRoutes = [
  {
    path: "/presence-verification",
    title: getDocumentTitle("Presence Verification"),
    Component: () => <PresenceVerificationLanding program="rvd" />,
    eventName: "RVDPresenceVerification",
  },
  {
    path: "/getStarted",
    title: getDocumentTitle("Get Started"),
    Component: GetStarted,
  },
  {
    path: "/document-upload",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicDashboard applicationType="rvd" />,
    eventName: "RVDDashboard",
  },
  {
    path: "/final-confirmation",
    title: getDocumentTitle("Final Confirmation"),
    Component: () => <DynamicFinalConfirmation applicationType="rvd" />,
    eventName: "RVDFinalConfirmation",
  },
];
export const rpcRoutes = [
  {
    path: "/presence-verification",
    title: getDocumentTitle("Presence Verification"),
    Component: () => <PresenceVerificationLanding program="rpc" />,
    eventName: "RPCPresenceVerification",
  },
  {
    path: "/document-upload",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicDashboard applicationType="rpc" />,
    eventName: "RPCDashboard",
  },
  {
    path: "/final-confirmation",
    title: getDocumentTitle("Final Confirmation"),
    Component: () => <DynamicFinalConfirmation applicationType="rpc" />,
    eventName: "RPCFinalConfirmation",
  },
  {
    path: "/getStarted",
    title: getDocumentTitle("Get Started"),
    Component: GetStarted,
  },
];
export const cecRoutes = [
  {
    path: "/",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicAppsDashboard applicationType="cec" />,
    eventName: "CECDashboard",
  },
  {
    path: "/result",
    title: getDocumentTitle("Eligibility Result"),
    Component: () => <DynamicFinalConfirmation applicationType="cec" />,
    eventName: "CECResult",
  },
  {
    path: "/error",
    title: getDocumentTitle("Error"),
    Component: DynamicErrorScreen,
    eventName: "CECError",
  },
  {
    path: "/under-maintenance",
    title: "Under Maintenance",
    Component: UnderMaintenance,
    eventName: "CECUnderMaintenance",
  },
];
export const nchRoutes = [
  {
    path: "",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicAppsDashboard applicationType="nch" />,
    eventName: "NCHDashboard",
  },
  {
    path: "/landing",
    title: getDocumentTitle("NCHPaymentLanding"),
    Component: () => <NCHPaymentLanding applicationType="nch" />,
    eventName: "NCHPaymentLanding",
  },
  {
    path: "/presence-verification",
    title: getDocumentTitle("Presence Verification"),
    Component: () => <PresenceVerificationLanding program="nch" />,
    eventName: "NCHPresenceVerification",
  },
  {
    path: "/document-upload",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicAppsDashboard applicationType="nch" />,
    eventName: "NCHDashboard",
  },
  {
    path: "/getStarted",
    title: getDocumentTitle("Get Started"),
    Component: GetStarted,
  },
  {
    path: "/final-confirmation",
    title: getDocumentTitle("Final Confirmation"),
    Component: () => <FinalConfirmation applicationType="nch" />,
    eventName: "NCHFinalConfirmation",
  },
  {
    path: "/name-confirmation",
    title: getDocumentTitle("Name Change Document"),
    Component: GenericNameConfirmation,
    eventName: "NCHNameConfirmation",
  },
  {
    path: "/name-changes",
    title: getDocumentTitle("Name Change Documents"),
    Component: GenericNameChanges,
    eventName: "NCHNameChanges",
  },
  {
    path: "/appointment-location",
    title: getDocumentTitle("Appointment Location"),
    Component: FieldOffices,
    eventName: "NCHAppointmentLocation",
  },
  {
    path: "/appointments",
    title: getDocumentTitle("Appointments"),
    Component: Appointments,
    eventName: "NCHAppointments",
  },
  {
    path: "/saved",
    title: getDocumentTitle("Saved"),
    Component: () => <SaveAndExit />,
    eventName: "NCHdocumentVerificationComplete",
  },
  {
    path: "/log-out",
    title: "Log Out",
    Component: () => <SaveAndExit isLogout />,
    eventName: "NCHLogout",
  },
];
export const rncRoutes = [
  {
    path: "",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicAppsDashboard applicationType="rnc" />,
    eventName: "RNCDashboard",
  },
  {
    path: "/document-upload",
    title: getDocumentTitle("Dashboard"),
    Component: () => <DynamicAppsDashboard applicationType="rnc" />,
    eventName: "RNCDashboard",
  },
  {
    path: "/landing",
    title: getDocumentTitle("RNCPaymentLanding"),
    Component: () => <RNCPaymentLanding applicationType="rnc" />,
    eventName: "RNCPaymentLanding",
  },
  {
    path: "/presence-verification",
    title: getDocumentTitle("Presence Verification"),
    Component: () => <PresenceVerificationLanding program="rnc" />,
    eventName: "RNCPresenceVerification",
  },
  {
    path: "/final-confirmation",
    title: getDocumentTitle("Final Confirmation"),
    Component: () => <FinalConfirmation applicationType="rnc" />,
    eventName: "RNCFinalConfirmation",
  },
  {
    path: "/name-confirmation",
    title: getDocumentTitle("Name Change Document"),
    Component: GenericNameConfirmation,
    eventName: "RNCNameConfirmation",
  },
  {
    path: "/name-changes",
    title: getDocumentTitle("Name Change Documents"),
    Component: GenericNameChanges,
    eventName: "RNCNameChanges",
  },
  {
    path: "/appointment-location",
    title: getDocumentTitle("Appointment Location"),
    Component: FieldOffices,
    eventName: "RNCAppointmentLocation",
  },
  {
    path: "/appointments",
    title: getDocumentTitle("Appointments"),
    Component: Appointments,
    eventName: "RNCAppointments",
  },
  {
    path: "/saved",
    title: getDocumentTitle("Saved"),
    Component: () => <SaveAndExit />,
    eventName: "RNCdocumentVerificationComplete",
  },
  {
    path: "/log-out",
    title: "Log Out",
    Component: () => <SaveAndExit isLogout />,
    eventName: "RNCLogout",
  },
];
export default routes;
